import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Trash2, Plus, Search, Globe2, Check } from 'lucide-react';
import { request } from '../../api/client';
import { Card, Button, Input, Field, DataTable, Tag, cn, toast } from '../../ui';
import type { DataTableColumn } from '../../ui';
import { INDIAN_STATES, STATE_NAME } from '../../constants/indianStates';

type Holiday = {
  id: string;
  holidayDate: string;
  name: string;
  appliesToAll: boolean;
  stateCodes?: string[] | null;
};

/**
 * Company holiday calendar (ATTENDANCE.MANAGE). A holiday is NATIONAL (applies to every rep, e.g.
 * Diwali) or restricted to specific STATES (a regional holiday only suppresses "Absent" for reps whose
 * territory is in those states). Tailwind-only (Green Bay ui kit). Editing the calendar refetches
 * attendance so absences recompute immediately.
 */
export function HolidaysCard() {
  const qc = useQueryClient();
  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [appliesToAll, setAppliesToAll] = useState(true);
  const [states, setStates] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  const { data: holidays, isLoading } = useQuery({
    queryKey: ['holidays'],
    queryFn: () => request<Holiday[]>({ url: '/holidays' }),
  });

  // The state-wise picker only offers states that exist in the territory tree (where the company
  // actually operates) — a regional holiday for a state with no reps would be meaningless.
  const { data: territoryStates } = useQuery({
    queryKey: ['territory-states'],
    queryFn: () => request<string[]>({ url: '/holidays/states' }),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['holidays'] });
    qc.invalidateQueries({ queryKey: ['attendance'] }); // absences depend on the calendar
  };
  const reset = () => { setDate(''); setName(''); setAppliesToAll(true); setStates([]); setSearch(''); };

  const addMut = useMutation({
    mutationFn: () => request({
      url: '/holidays', method: 'POST',
      data: { date, name: name.trim(), appliesToAll, stateCodes: appliesToAll ? [] : states },
    }),
    onSuccess: () => { toast.success('Holiday added'); reset(); refresh(); },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'Failed to add holiday'),
  });
  const delMut = useMutation({
    mutationFn: (id: string) => request({ url: `/holidays/${id}`, method: 'DELETE' }),
    onSuccess: () => { toast.success('Holiday removed'); refresh(); },
    onError: (e: any) => toast.error(e.response?.data?.error?.message ?? 'Failed to remove holiday'),
  });

  const canAdd = !!date && !!name.trim() && (appliesToAll || states.length > 0);
  const toggleState = (code: string) =>
    setStates((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const availableStates = useMemo(
    () => INDIAN_STATES.filter((s) => (territoryStates ?? []).includes(s.code)),
    [territoryStates],
  );
  const filteredStates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return availableStates.filter((s) => !q || s.name.toLowerCase().includes(q) || s.code.includes(q));
  }, [search, availableStates]);

  const columns: DataTableColumn<Holiday>[] = [
    {
      key: 'holidayDate', title: 'Date', dataIndex: 'holidayDate', width: 200, sortable: true,
      render: (v: string) => <span className="font-medium text-content">{dayjs(v).format('ddd, DD MMM YYYY')}</span>,
    },
    { key: 'name', title: 'Holiday', dataIndex: 'name', render: (v: string) => <span className="text-content">{v}</span> },
    {
      key: 'scope', title: 'Applies to',
      render: (_: any, r: Holiday) =>
        r.appliesToAll ? (
          <Tag tone="green"><Globe2 className="mr-1 inline size-3" /> All India</Tag>
        ) : (
          <div className="flex flex-wrap gap-1">
            {(r.stateCodes ?? []).map((c) => <Tag key={c} tone="blue">{STATE_NAME.get(c) ?? c}</Tag>)}
          </div>
        ),
    },
    {
      key: 'del', title: '', align: 'right' as const, width: 60,
      render: (_: any, r: Holiday) => (
        <Button
          variant="soft-danger" size="sm" icon={<Trash2 className="size-4" />}
          loading={delMut.isPending && delMut.variables === String(r.id)}
          onClick={() => { if (window.confirm(`Remove "${r.name}"?`)) delMut.mutate(String(r.id)); }}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <p className="max-w-3xl text-sm text-muted">
        Declared company holidays. A field rep is never marked <b className="text-content">Absent</b> on a holiday that
        applies to their state — it reads like the Sunday weekly off. National holidays apply to everyone; regional ones
        only to reps whose territory is in the chosen states.
      </p>

      {/* ── Add a holiday ── */}
      <Card title="Add a holiday">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-40">
            <Field label="Date" required>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
          </div>
          <div className="w-full sm:w-60">
            <Field label="Holiday name" required>
              <Input
                value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Diwali"
                onKeyDown={(e) => { if (e.key === 'Enter' && canAdd) addMut.mutate(); }}
              />
            </Field>
          </div>
          <Field label="Applies to">
            <div className="inline-flex rounded-control border border-line bg-surface-2/50 p-1">
              {([{ v: true, label: 'All India' }, { v: false, label: 'Specific states' }] as const).map((o) => (
                <button
                  key={String(o.v)} type="button" onClick={() => setAppliesToAll(o.v)}
                  className={cn(
                    'rounded-[7px] px-3 py-1.5 text-sm font-medium transition-colors',
                    appliesToAll === o.v ? 'bg-surface text-content shadow-soft' : 'text-muted hover:text-content',
                  )}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </Field>
          <Button variant="primary" icon={<Plus className="size-4" />} disabled={!canAdd} loading={addMut.isPending} onClick={() => addMut.mutate()}>
            Add holiday
          </Button>
        </div>

        {!appliesToAll && (
          <div className="mt-3 max-w-xl overflow-hidden rounded-control border border-line">
            <div className="flex items-center gap-2 border-b border-line bg-surface-2/40 px-3 py-2">
              <Search className="size-4 shrink-0 text-muted" />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search states…"
                className="w-full bg-transparent text-sm text-content placeholder:text-subtle focus:outline-none"
              />
              <span className="shrink-0 text-xs text-muted">{states.length} selected</span>
              {states.length > 0 && (
                <button type="button" onClick={() => setStates([])} className="shrink-0 text-xs font-semibold text-brand-600 hover:text-brand-700">Clear</button>
              )}
            </div>
            <div className="max-h-56 overflow-y-auto p-2">
              <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
                {filteredStates.map((s) => {
                  const on = states.includes(s.code);
                  return (
                    <button
                      key={s.code} type="button" onClick={() => toggleState(s.code)}
                      className={cn(
                        'flex items-center gap-2 rounded-[8px] px-2 py-1.5 text-left text-sm transition-colors',
                        on ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'text-content hover:bg-surface-2',
                      )}
                    >
                      <span className={cn('grid size-4 shrink-0 place-items-center rounded border', on ? 'border-brand-600 bg-brand-600 text-white' : 'border-line')}>
                        {on && <Check className="size-3" />}
                      </span>
                      <span className="truncate">{s.name}</span>
                    </button>
                  );
                })}
                {!filteredStates.length && (
                  <div className="col-span-full px-2 py-4 text-center text-sm text-muted">
                    {territoryStates == null
                      ? 'Loading states…'
                      : availableStates.length === 0
                        ? 'No operating states in the territory tree yet.'
                        : `No states match “${search}”.`}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ── List ── */}
      <DataTable
        storageKey="holidays"
        rowKey="id"
        loading={isLoading}
        dataSource={holidays ?? []}
        columns={columns}
        pagination={false}
        emptyText="No holidays declared yet"
      />
    </div>
  );
}
